// Ad fill: whether each Adsterra slot whose frame was made actually showed an ad, counted without identifiers.
// ad-slots.js (loadSlot, onFrameMessage) feeds a tracker here; each slot is classified once:
//   filled    any of its frame's reports said filled (worker/src/ads.js REPORT_SCRIPT reports about 4 s and 9 s after load)
//   empty     two reports, both not filled (no fill, or a blocker stopped the ad's scripts inside the frame)
//   noreport  no report within NOREPORT_MS of the frame being made (the frame itself was blocked, or the tab closed), or
//             the page went away before the reports were complete
// A frame our own page removed before it was classified (the app redrew the page) is dropped, not counted.
// Results go out with navigator.sendBeacon to /api/ads/fill on the page's own host (worker/src/ad-fill.js), batched per
// page: each one's unit (native, 300x250, …), the slot type (from the path: SLOT_TYPES) and the result. No user id, no
// URL; the Worker takes the site from the host and keeps only daily totals.

export const FILL_ENDPOINT = '/api/ads/fill';
export const FILL_RESULTS = ['filled', 'empty', 'noreport'];
export const SLOT_TYPES = ['article', 'ward', 'matter', 'meeting', 'meetings', 'election', 'home', 'other'];
export const NOREPORT_MS = 15_000;
/** The most slot results one beacon carries (the Worker caps it the same: worker/src/ad-fill.js). */
export const MAX_BATCH = 20;
const SEND_AFTER_MS = 2_000;

/**
 * A slot's type, from the page's path (never the path itself): an On My Block article, a ward/district or member page,
 * a matter (vote, legislation), a meeting, the meetings hub, the election guide, the homepage, anything else.
 */
export function slotTypeOf(path, { article = false } = {}) {
  const p = String(path || '/').toLowerCase();
  if (article || /^\/on-my-block\/./.test(p)) return 'article';
  if (/^\/(?:ward|district|member|alderman|alderperson|council-member)s?\/./.test(p)) return 'ward';
  if (/^\/(?:matter|vote|legislation)\/./.test(p)) return 'matter';
  if (/^\/meeting\/./.test(p)) return 'meeting';
  if (/^\/meetings\/?$/.test(p)) return 'meetings';
  if (/elect|elecc|wybor|candidat|kandyd/.test(p)) return 'election';
  if (p === '/' || p === '') return 'home';
  return 'other';
}

/**
 * One slot's result from its reports so far: { n: reports received, filled: any said filled, created: ms }, or null
 * while it can still change. `final`: the page is going away, so anything unresolved is noreport.
 */
export function classifySlot(r, now, final = false) {
  if (r.filled) return 'filled';
  if (r.n >= 2) return 'empty';
  if (final) return 'noreport';
  if (r.n === 0 && now - r.created >= NOREPORT_MS) return 'noreport';
  return null;
}

/**
 * The page's tracker. `send(body)`: posts one JSON string (sendBeacon in the browser). The clock and timers are
 * injectable for the tests. created(key, {unit, type}) when a frame is made; report(key, filled) per frame report;
 * flush(final) on visibilitychange (false) and pagehide (true).
 */
export function fillTracker({ send, now = () => Date.now(), setTimer = (f, ms) => setTimeout(f, ms), connected = () => true } = {}) {
  const pending = new Map();   // key -> { unit, type, n, filled, created }
  let queue = [];
  let timer = null;
  const done = (key, rec, result) => {
    pending.delete(key);
    queue.push({ unit: rec.unit, type: rec.type, result });
    if (!timer) timer = setTimer(() => { timer = null; flush(false); }, SEND_AFTER_MS);
  };
  const check = (key, final = false) => {
    const rec = pending.get(key);
    if (!rec) return;
    // Our own page took the frame away (a redraw) before it reported: not a fill result at all.
    if (!final && !connected(key)) { pending.delete(key); return; }
    const result = classifySlot(rec, now(), final);
    if (result) done(key, rec, result);
  };
  function flush(final) {
    if (final) for (const key of [...pending.keys()]) { if (connected(key)) check(key, true); else pending.delete(key); }
    while (queue.length) {
      const batch = queue.slice(0, MAX_BATCH);
      queue = queue.slice(MAX_BATCH);
      try { send(JSON.stringify({ slots: batch })); } catch { /* a lost beacon is a lost count */ }
    }
  }
  return {
    created(key, { unit, type }) {
      if (pending.has(key)) return;
      pending.set(key, { unit, type, n: 0, filled: false, created: now() });
      setTimer(() => check(key), NOREPORT_MS);
    },
    report(key, filled) {
      const rec = pending.get(key);
      if (!rec) return;
      rec.n += 1;
      rec.filled ||= !!filled;
      check(key);
    },
    flush,
    get pending() { return pending.size; },
    get queued() { return queue.length; },
  };
}

let page = null;
/** The browser's tracker (one per page load), sending to FILL_ENDPOINT on this host; null outside a browser. */
export function pageFillTracker(isConnected) {
  if (page || typeof window === 'undefined' || typeof navigator === 'undefined' || !navigator.sendBeacon) return page;
  page = fillTracker({
    send: (body) => navigator.sendBeacon(FILL_ENDPOINT, new Blob([body], { type: 'application/json' })),
    connected: isConnected,
  });
  addEventListener('pagehide', () => page.flush(true));
  addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') page.flush(false); });
  return page;
}
